/**
 * @fileOverview Moving between steps: finding a step's region, landing on it, keeping it in shot while
 * it is up — and the move itself, from one step to the next showable one, whatever view it is on.
 */

import { logger } from "@/services/logger"
import {
  anchorRejection,
  findAnchors,
  isAnchorVisible,
  needsScroll,
  scrollAnchorIntoView,
  unionRect,
  visibleRegion,
  waitForAnchors,
  waitForVisible,
  waitForStableRect,
  type Rect,
} from "@/services/viewHelp/target"
import { readFrameTrail } from "@/services/viewHelp/trail"
import type { ViewHelpStep } from "@/services/viewHelp/types"
import {
  ANCHOR_TIMEOUT_MS,
  FOLLOW_MS,
  IN_FRAME_NAV_TIMEOUT_MS,
  SCROLL_MS,
  SEEK_CAMERA_MS,
  TRACK_MS,
  ZOOM_MS,
  prefersReducedMotion,
  sleep,
} from "./timing"
import {
  Move,
  cameraMs,
  elapsed,
  endShown,
  focusRect,
  frameTrail,
  frameWindow,
  hold,
  isActive,
  isBusy,
  isFrameLoading,
  landed,
  motionMs,
  playing,
  scaled,
  skipped,
  stepIndex,
  targetIndex,
  worked,
} from "./state"
import {
  displayIndex,
  dwellMs,
  firstPlayableFrom,
  playable,
  sectionOf,
  sections,
  steps,
  stepsInSection,
  targetOf,
} from "./tour"
import { aimCamera, followRegion, pullBackBeforeScrolling } from "./camera"
import { isResting, sendPointerAway } from "./pointer"
import { actualFrameRoute, showRouteInFrame } from "./frame"
import { isInAnotherScene, leaveScene, prepareScene, sceneIsAheadOf } from "./scenes"

/**
 * How a move is paced depends on who asked for it.
 *
 *   - `auto` — the clock ran out. The full scene: the pointer walks to a tab when the view changes, and
 *     the camera glides. This is the walkthrough telling its story. It waits its turn: a move already
 *     under way is not overtaken by the clock.
 *   - `step` — the reader clicked the dot or pressed an arrow key. The same scene, and it overtakes a
 *     move already under way instead of being ignored.
 *   - `seek` — the reader clicked the timeline. A seek in a video player is a cut, and so is this: no
 *     walk to the tab (the frame is told to navigate at once), no glide inside the frame, a short
 *     camera move, and earlier steps' actions replayed at speed. Somebody who picked a step on the
 *     timeline wants to be there, not to watch the way there — but the step itself, its own
 *     demonstration included, plays as it always does.
 */
export type MoveMode = "auto" | "step" | "seek"

/** What `resolveStep` found: a region to land on, a step with nothing anchored, or nothing to show. */
type Resolution = Rect | "centred" | null

/**
 * Finds a step's region inside the frame, or returns null if it cannot be shown there.
 *
 * The region is glided into view before the visibility test — or everything below the frame's fold
 * would be refused for being off screen — and measured only after it has stopped moving.
 */
async function resolveStep(
  candidate: ViewHelpStep,
  budgetMs: number,
  glideMs: number,
  move: Move
): Promise<Resolution> {
  const win = frameWindow.value
  if (!win) return null
  if (!candidate.anchor) return "centred"

  const found = await waitForAnchors(win, candidate.anchor, budgetMs, () => !move.current)
  if (!found.length) {
    // Often right — a region behind a `v-if` that this reader's data does not render — but in a
    // tour's own sample data it is the commonest authoring mistake, so development says so.
    if (move.current && import.meta.env.DEV) {
      console.warn(
        `[view-help] step "${candidate.key}": anchor "${candidate.anchor}" not found on ${win.location.pathname} — skipped`
      )
    }
    return null
  }

  if (candidate.requireAnchor && !findAnchors(win, candidate.requireAnchor).some(el => isAnchorVisible(win, el))) {
    if (import.meta.env.DEV) {
      console.warn(`[view-help] step "${candidate.key}": required "${candidate.requireAnchor}" not visible — skipped`)
    }
    return null
  }

  // A step's region keeps room below it for its dot and its text — see `TEXT_ROOM_SHARE`.
  const room = { roomBelow: true }
  if (glideMs > 0) await pullBackBeforeScrolling(win, found[0], move, needsScroll(win, found[0], room))
  if (!move.current) return null
  await scrollAnchorIntoView(win, found[0], prefersReducedMotion() ? 0 : glideMs, () => !move.current, room)
  if (!move.current) return null
  await waitForStableRect(win, found[0])
  // A region that is fading in, or about to, is waited for rather than refused as hidden.
  await waitForVisible(win, found[0], undefined, () => !move.current)
  if (!move.current) return null

  const visible = found.filter(el => isAnchorVisible(win, el))
  if (!visible.length) {
    // Found but unpointable — the case the deleted spotlight tour could not see, so the one worth
    // being loud about: a healthy box that is nowhere on screen.
    const box = found[0].getBoundingClientRect()
    const size = `${Math.round(box.width)}×${Math.round(box.height)}`
    const at = `${Math.round(box.left)},${Math.round(box.top)}`
    const detail = `anchor "${candidate.anchor}" rejected as ${anchorRejection(win, found[0])} (${size} at ${at})`
    logger.log(`View help step "${candidate.key}": ${detail}`)
    if (import.meta.env.DEV) console.warn(`[view-help] step "${candidate.key}": ${detail}`)
    return null
  }

  return unionRect(visible)
}

/**
 * Takes the current step down: its dot and text go, and any hold on them is released.
 *
 * The release matters. The hold is set by hovering the dot or the text, and cleared on `mouseleave` —
 * but an element that is removed from under the mouse never fires `mouseleave`. Without clearing it
 * here, somebody whose pointer happened to be on the dot when the step changed would leave the clock
 * held for good, and the walkthrough would silently stop advancing.
 */
function leaveStep() {
  landed.value = false
  hold.value = false
}

/**
 * Commits a step: the camera moves onto its region, and the dot and the text appear only once it has
 * arrived. The eye follows the movement, so by the time there is something to read the reader is
 * already looking at the thing it is about.
 */
async function land(index: number, rect: Rect | null, move: Move, mode: MoveMode) {
  leaveStep()
  elapsed.value = 0
  stepIndex.value = index
  targetIndex.value = null
  // A step that keeps to the whole page caps the zoom at 1: the camera shows everything, and the dot
  // and the text still go to its region.
  aimCamera(rect, motionMs(mode === "seek" ? SEEK_CAMERA_MS : ZOOM_MS), steps.value[index]?.zoom === false ? 1 : null)
  // Whatever the pointer just did is done; what is left is to read. It fades as the camera moves —
  // unless the step ended by resting it on something (`hover`): what shows only under the mouse is
  // what that step is about, so the pointer stays on it until the next step takes it away.
  const actions = steps.value[index]?.actions ?? []
  const ended = actions[actions.length - 1]
  if (!(ended?.do === "hover" && isResting())) sendPointerAway()

  if (rect && cameraMs.value && !(await move.pause(cameraMs.value))) return

  landed.value = true
  // Now, not earlier: a step's own demonstration has run by this point, so a tab the pointer just
  // pressed is the one the trail names.
  frameTrail.value = frameWindow.value ? readFrameTrail(frameWindow.value) : []
  const candidate = steps.value[index]
  if (rect && candidate) void trackRegion(candidate, move)
}

/**
 * Keeps the camera, the dot and the text on a step's region for as long as the step is up.
 *
 * The region is measured once, as the step lands, and the page does not always hold still after
 * that: a filter re-lays-out the table under it, switching the analysis from chart to table swaps one
 * for the other in stages, the app scrolls a list into view after a click. The dot was then left at
 * the region's old position — pointing at the wrong thing. So the region is re-measured every
 * `TRACK_MS`, and the camera follows over `FOLLOW_MS` when it has moved. It ends by itself when the
 * step does.
 */
async function trackRegion(candidate: ViewHelpStep, move: Move) {
  const win = frameWindow.value
  if (!win || !candidate.anchor) return

  while (move.current && landed.value) {
    await sleep(TRACK_MS)
    if (!move.current || !landed.value) return
    const rect = visibleRegion(win, candidate.anchor)
    const current = focusRect.value
    if (!rect || !current) continue
    const moved =
      Math.abs(rect.top - current.top) > 1 ||
      Math.abs(rect.left - current.left) > 1 ||
      Math.abs(rect.width - current.width) > 1 ||
      Math.abs(rect.height - current.height) > 1
    if (moved) followRegion(rect, prefersReducedMotion() ? 0 : FOLLOW_MS)
  }
}

/**
 * Tries to show one step: out of a scene it is not part of, onto its view, into its scene, onto its
 * region. `landed` when it is up, `skipped` when it cannot be shown (and is marked so), `stopped` when
 * the move was overtaken on the way.
 */
async function tryStep(index: number, move: Move, mode: MoveMode): Promise<"landed" | "skipped" | "stopped"> {
  const seeking = mode === "seek"
  const glideMs = seeking ? 0 : scaled(SCROLL_MS)
  const skip = () => {
    skipped.value.add(index)
    return "skipped" as const
  }

  targetIndex.value = index
  const candidate = steps.value[index]

  // Out of the scene first, while its controls are still on this page — before a change of view
  // would take them away and leave nothing to press "Abbrechen" on. Always at speed: tidying up
  // is not part of the story, and watching the pointer travel to "Abbrechen" at the end of every
  // scene would add a second and a half of nothing, thirteen times.
  if (isInAnotherScene(candidate)) {
    await leaveScene(move, false, candidate.route !== actualFrameRoute())
    if (!move.current) return "stopped"
  }

  const hopped = candidate.route !== actualFrameRoute()
  if (hopped) {
    const shown = await showRouteInFrame(targetOf(candidate), move, !seeking)
    // Overtaken, not failed: the step is fine, somebody simply went elsewhere.
    if (!move.current) return "stopped"
    if (!shown) return skip()
  }

  // Into the state the step is explained in: its scene, the scene's earlier steps, its own
  // actions. See `prepareScene` for what is played and what is only done.
  const prepared = await prepareScene(candidate, index, move)
  if (!move.current) return "stopped"
  if (!prepared) return skip()

  const budget = hopped ? IN_FRAME_NAV_TIMEOUT_MS : ANCHOR_TIMEOUT_MS
  const resolved = await resolveStep(candidate, budget, glideMs, move)
  if (!move.current) return "stopped"
  if (resolved === null) return skip()

  await land(index, resolved === "centred" ? null : resolved, move, mode)
  return "landed"
}

/**
 * Puts the step that is up back the way it should be, after a move that found nothing to move to.
 *
 * When the frame is still on its view and in its scene, only the camera needs setting: the region is
 * measured again and aimed at with the step's own zoom. Otherwise the step is shown again from the
 * start, as any other step is — which also takes the frame out of the scene an overtaken move had
 * entered.
 */
async function restoreStep(move: Move, mode: MoveMode) {
  const current = steps.value[stepIndex.value]
  const win = frameWindow.value
  if (!current || !win) {
    landed.value = focusRect.value !== null || !current?.anchor
    return
  }

  if (isInAnotherScene(current) || sceneIsAheadOf(current, stepIndex.value) || current.route !== actualFrameRoute()) {
    if ((await tryStep(stepIndex.value, move, mode)) !== "skipped") return
    targetIndex.value = null
    return
  }

  const rect = current.anchor ? visibleRegion(win, current.anchor) : null
  aimCamera(rect, motionMs(SEEK_CAMERA_MS), current.zoom === false ? 1 : null)
  // The move cleared the header's record and tab when it started; read them back from the frame.
  frameTrail.value = readFrameTrail(win)
  landed.value = rect !== null || !current.anchor
}

/**
 * Puts the end screen up over the last step, with that step counted as read — so the player's button
 * offers "Von vorn", as it does after the reading time ran out by itself.
 */
export function showEnd() {
  playing.value = false
  elapsed.value = dwellMs.value
  endShown.value = true
}

/** Settles when the latest move has finished everything it does in the frame — see `moveFrom`. */
let windingDown: Promise<void> = Promise.resolve()

/**
 * Stops waiting for moves of a walkthrough that is gone — for a close and a new start. A move overtaken
 * by the close can be left inside a wait on the frame that was just removed; the next walkthrough's
 * first move must not queue behind it.
 */
export function forgetMoves() {
  windingDown = Promise.resolve()
}

/**
 * Walks to the first showable step at or after `start` in `direction`, moving the frame to another
 * view where the catalog asks for one. `mode` decides the pace — see {@link MoveMode}.
 *
 * The skip loop is the deleted spotlight tour's, near enough unchanged; the loop was never the bug.
 * It never *fired*, because its visibility test accepted an element parked outside the viewport, so
 * nothing ever looked missing. `isAnchorVisible` is what makes it work.
 */
export async function moveFrom(start: number, direction: 1 | -1, mode: MoveMode) {
  if (!isActive.value || isFrameLoading.value) return
  if (isBusy.value && mode === "auto") return

  const move = new Move()
  // Any move leaves the end screen: the reader has gone back into the walkthrough.
  endShown.value = false
  // Claimed at once, so a move started after this one waits for this one in turn.
  const previous = windingDown
  let wound!: () => void
  windingDown = new Promise(resolve => (wound = resolve))
  isBusy.value = true
  // The timeline answers now, before anything in the frame has had to move.
  targetIndex.value = firstPlayableFrom(start, direction)
  // This step's demonstration starts here: everything from now until it lands is its own time.
  worked.value = 0
  // The record and tab in the header belong to the page being left. The view's own name, which comes
  // from the catalog, carries the header until the step lands and the frame can be read again.
  frameTrail.value = []

  try {
    // The step being left is gone from here on. Without this its dot and text would stay up — and on
    // a hop they would jump onto the tab the camera turns to, still saying the old thing.
    leaveStep()

    // The move this one overtook stops at its next check — but until then it is still acting in the
    // frame. Starting at once had two moves at work side by side: one pressing a scene's "Abbrechen"
    // while the other's click reopened the dialog, one's hold on the camera released by the other's
    // `finally`. So the frame is only touched once the overtaken move has wound down. Every wait in a
    // move ends as soon as it is overtaken, so this is a beat, not a stall.
    await previous
    if (!move.current) return

    for (let index = start; index >= 0 && index < steps.value.length; index += direction) {
      if (skipped.value.has(index)) continue
      const outcome = await tryStep(index, move, mode)
      if (outcome !== "skipped") return
    }

    // Ran off the end. Forwards, that is the natural end of the walkthrough; backwards, there is
    // nothing earlier to show. Either way the step that was up stays up — but not necessarily as it
    // was left: this move may have overtaken one that had already begun on another step, entered its
    // scene or turned the camera. Declaring the old step landed as it stood left the camera on the
    // whole page with a zoomed step's dot on it, or the other step's dialog open over it.
    targetIndex.value = null
    if (direction === 1) playing.value = false
    await restoreStep(move, mode)
    // → past the last step is the reader saying they are done with it: the end screen, as when the
    // last step's reading time runs out.
    if (direction === 1 && move.current) showEnd()
  } finally {
    if (move.current) isBusy.value = false
    wound()
  }
}

// ── what the reader can ask for ─────────────────────────────────────────────

// From the step being moved to, not the one on screen: pressing → three times while the first move is
// still under way means three steps on, not the same step asked for three times.
export const next = () => moveFrom(displayIndex.value + 1, 1, "step")
export const previous = () => moveFrom(displayIndex.value - 1, -1, "step")

/** Jump to any step on the timeline, back or ahead — at once, overtaking whatever was under way. */
export function seek(index: number) {
  if (index === stepIndex.value && !isBusy.value) {
    // Onto the step already up — from the end screen too, whose page button or "Von vorn" can name
    // exactly that step: the screen goes, as it does for any other move, and the step is read again.
    elapsed.value = 0
    endShown.value = false
    return
  }
  const from = targetIndex.value ?? stepIndex.value
  void moveFrom(index, index < from ? -1 : 1, "seek")
}

/** Jump to the first step of a section — a digit key, or the next or previous section. */
export function seekSection(section: string) {
  const first = stepsInSection(section)[0]
  if (first !== undefined) seek(first)
}

/** The next or previous view. Nothing past either end: there is no view to go to. */
export function stepSection(direction: 1 | -1) {
  const list = sections.value
  const section = list[list.indexOf(sectionOf(steps.value[displayIndex.value]) ?? "") + direction]
  if (section) seekSection(section)
}

/** The very first or the very last step of the whole walkthrough. */
export function seekEdge(edge: "first" | "last") {
  const list = playable.value
  const index = edge === "first" ? list[0] : list[list.length - 1]
  if (index !== undefined) seek(index)
}
